// FRACAS Detail Page - COMPLETE with Lab Testing Integration & IATF Compliance
// ============================================================

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useFracas } from '../contexts/FracasContext';
import { useLabSample } from '../contexts/LabSampleContext';
import { AlertTriangle } from 'lucide-react';
import { LabRequestModal } from '../components/fracas/LabRequestModal';
import { FracasDetailHeader } from '../components/fracas/FracasDetailHeader';
import { FracasCoreInfo } from '../components/fracas/FracasCoreInfo';
import { FracasCompliance } from '../components/fracas/FracasCompliance';
import { FracasLinkedRecords } from '../components/fracas/FracasLinkedRecords';
import { FracasSidebar } from '../components/fracas/FracasSidebar';

export const FracasDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { getIncidentById, deleteIncident } = useFracas();
    const { samples, addSample } = useLabSample();
    const incident = getIncidentById(id || '');
    const [isEditing, setIsEditing] = useState(false);
    const [showLabRequestModal, setShowLabRequestModal] = useState(false);

    // Get linked lab samples - matching by id (not "FRAC-" prefix)
    const linkedLabSamples = samples.filter(s => s.fracasIncidentId === id);

    useEffect(() => {
        if (!incident) {
            navigate('/fracas');
        }
    }, [incident, navigate]);

    if (!incident) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <AlertTriangle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Incident Not Found</h2>
                    <Link to="/fracas" className="text-blue-600 hover:text-blue-800">
                        ← Back to FRACAS List
                    </Link>
                </div>
            </div>
        );
    }

    const handleDelete = async () => {
        if (window.confirm('Are you sure you want to delete this incident?')) {
            try {
                await deleteIncident(incident.id);
                navigate('/fracas');
            } catch (err) {
                console.error("Failed to delete incident:", err);
                alert("Failed to delete incident. Please try again.");
            }
        }
    };

    const handleCreateLabSample = () => {
        // Create lab sample linked to this FRACAS
        addSample({
            sampleType: 'warranty_return',
            product: incident.productName || incident.title, // Use proper product name
            serialNumber: `FRAC-${id}-SAMPLE`,
            submittedBy: 'Quality Team',
            submittedDate: new Date().toISOString().split('T')[0],
            receivedDate: new Date().toISOString().split('T')[0],
            source: `FRACAS Incident #${id}`,
            fracasIncidentId: id,  // Link to this FRACAS
            priority: incident.severity === 'Critical' ? 'critical' : 'high',
            requestedTests: ['Failure Analysis', 'Root Cause Investigation'],
            testScheduleDate: new Date(Date.now() + 86400000).toISOString().split('T')[0],
            testDueDate: new Date(Date.now() + 3 * 86400000).toISOString().split('T')[0],
            assignedLab: 'Quality Lab',
            status: 'received',
            notes: `Lab sample created from FRACAS incident: ${incident.title}`,
        });
        setShowLabRequestModal(false);
        window.location.reload();
    };

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <LabRequestModal
                isOpen={showLabRequestModal}
                onClose={() => setShowLabRequestModal(false)}
                onCreate={handleCreateLabSample}
                incident={incident}
            />

            <FracasDetailHeader
                incident={incident}
                onBack={() => navigate('/fracas')}
                onEdit={() => setIsEditing(!isEditing)}
                onDelete={handleDelete}
                isEditing={isEditing}
            />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Content */}
                <div className="lg:col-span-2 space-y-6">
                    <FracasCoreInfo incident={incident} />
                    <FracasCompliance incident={incident} />
                    <FracasLinkedRecords
                        incident={incident}
                        linkedLabSamples={linkedLabSamples}
                        onRequestLab={() => setShowLabRequestModal(true)}
                    />
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                    <FracasSidebar incident={incident} />
                </div>
            </div>
        </div>
    );
};

export default FracasDetail;
